import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Skeleton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import dayjs from 'dayjs';
import {
  INSCRICOES_POR_LIGA_AGRUPADAS,
  type InscricoesPorLigaAgrupadasData,
  type InscricoesPorLigaAgrupadasVars,
} from '../../../services/inscricaoTorneioService';
import InscricaoStatusChip from '../../../components/admin/InscricaoStatusChip';
import { INSCRICAO_STATUS_FILTER_OPTIONS } from '../../../utils/inscricaoStatusDisplay';

const COLUMN_COUNT = 6;

const STATUS_OPTIONS: { label: string; value: string | null }[] = [
  { label: 'Todos', value: null },
  ...INSCRICAO_STATUS_FILTER_OPTIONS,
];

const formatDate = (value?: string | null) =>
  value ? (dayjs(value).isValid() ? dayjs(value).format('DD/MM/YYYY HH:mm') : '') : '';

interface InscricaoRow {
  inscricaoId: string;
  torneioId: string;
  torneioNome: string;
  categoriaId: string;
  categoriaNome: string;
  status: string;
  dataInscricao?: string | null;
  equipeNome: string;
}

interface LigaInscricoesTabProps {
  ligaId: string;
}

const LigaInscricoesTab: React.FC<LigaInscricoesTabProps> = ({ ligaId }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [torneioFilter, setTorneioFilter] = useState<string | null>(null);
  const [dataInicio, setDataInicio] = useState<string | null>(null);
  const [dataFim, setDataFim] = useState<string | null>(null);

  const { data, loading, error } = useQuery<
    InscricoesPorLigaAgrupadasData,
    InscricoesPorLigaAgrupadasVars
  >(INSCRICOES_POR_LIGA_AGRUPADAS, {
    variables: { ligaId },
    skip: !ligaId,
    fetchPolicy: 'no-cache',
  });

  const allRows = useMemo<InscricaoRow[]>(() => {
    const atletas = data?.inscricoesPorLigaAgrupadas;
    if (!atletas) return [];
    return atletas.flatMap((atleta) =>
      atleta.torneios.flatMap((torneio) =>
        torneio.categorias.map((cat) => ({
          inscricaoId: cat.inscricaoId,
          torneioId: torneio.torneioId,
          torneioNome: torneio.torneioNome,
          categoriaId: cat.categoriaId,
          categoriaNome: cat.categoriaNome,
          status: cat.status,
          dataInscricao: cat.dataInscricao,
          equipeNome: cat.equipe?.nome || '-',
        })),
      ),
    );
  }, [data]);

  const torneioOptions = useMemo(() => {
    const map = new Map<string, string>();
    allRows.forEach((r) => {
      map.set(r.torneioId, r.torneioNome);
    });
    return Array.from(map.entries())
      .map(([id, nome]) => ({ id, nome }))
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }, [allRows]);

  const hasDateError = !!(
    dataInicio &&
    dataFim &&
    dayjs(dataInicio).isAfter(dayjs(dataFim))
  );

  const filteredRows = useMemo(() => {
    let rows = allRows;

    if (statusFilter) {
      rows = rows.filter((r) => r.status === statusFilter);
    }

    if (torneioFilter) {
      rows = rows.filter((r) => r.torneioId === torneioFilter);
    }

    if (dataInicio && !hasDateError) {
      const start = dayjs(dataInicio).startOf('day');
      rows = rows.filter((r) => {
        return r.dataInscricao
          ? dayjs(r.dataInscricao).isAfter(start) || dayjs(r.dataInscricao).isSame(start)
          : false;
      });
    }

    if (dataFim && !hasDateError) {
      const end = dayjs(dataFim).endOf('day');
      rows = rows.filter((r) => {
        return r.dataInscricao
          ? dayjs(r.dataInscricao).isBefore(end) || dayjs(r.dataInscricao).isSame(end)
          : false;
      });
    }

    return rows;
  }, [allRows, statusFilter, torneioFilter, dataInicio, dataFim, hasDateError]);

  const paginatedRows = useMemo(
    () => filteredRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [filteredRows, page, rowsPerPage],
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value || null);
    setPage(0);
  };

  const handleTorneioChange = (value: string) => {
    setTorneioFilter(value || null);
    setPage(0);
  };

  const handleResetFilters = () => {
    setStatusFilter(null);
    setTorneioFilter(null);
    setDataInicio(null);
    setDataFim(null);
    setPage(0);
  };

  const linkStyle = { color: '#1976d2', textDecoration: 'none' as const };

  return (
    <>
      {/* Filter Bar */}
      <Box className="card w-100 border-0 bg-white shadow-xs p-3 mb-3">
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <TextField
              select
              label="Torneio"
              size="small"
              fullWidth
              value={torneioFilter || ''}
              onChange={(e) => handleTorneioChange(e.target.value)}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">Todos</MenuItem>
              {torneioOptions.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.nome}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <TextField
              select
              label="Status"
              size="small"
              fullWidth
              value={statusFilter || ''}
              onChange={(e) => handleStatusChange(e.target.value)}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || 'all'} value={opt.value || ''}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <TextField
              label="Data Início"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={dataInicio || ''}
              onChange={(e) => {
                setDataInicio(e.target.value || null);
                setPage(0);
              }}
              error={hasDateError}
              helperText={hasDateError ? 'Data início maior que data fim' : undefined}
            />
          </Box>
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <TextField
              label="Data Fim"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={dataFim || ''}
              onChange={(e) => {
                setDataFim(e.target.value || null);
                setPage(0);
              }}
              error={hasDateError}
            />
          </Box>
          <Box sx={{ flex: '0 1 auto' }}>
            <Button variant="outlined" color="inherit" size="small" onClick={handleResetFilters}>
              Limpar
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#6c757d' }}>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Torneio</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Categoria</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Equipe</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Data Inscrição</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }} align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, idx) => (
                <TableRow key={idx}>
                  {Array.from({ length: COLUMN_COUNT }).map((__, cidx) => (
                    <TableCell key={cidx}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : error ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT}>
                  <div className="alert alert-danger mb-0" role="alert">
                    Erro ao carregar inscrições. Tente novamente.
                  </div>
                </TableCell>
              </TableRow>
            ) : filteredRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center">
                  Nenhuma inscrição encontrada.
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map((r) => (
                <TableRow key={r.inscricaoId}>
                  <TableCell>
                    <Link to={`/tournamentsingle/${r.torneioId}`} style={linkStyle}>
                      {r.torneioNome}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link
                      to={`/tournament/${r.torneioId}/category/${r.categoriaId}`}
                      style={linkStyle}
                    >
                      {r.categoriaNome}
                    </Link>
                  </TableCell>
                  <TableCell>{r.equipeNome}</TableCell>
                  <TableCell>
                    <InscricaoStatusChip status={r.status} />
                  </TableCell>
                  <TableCell>{formatDate(r.dataInscricao)}</TableCell>
                  <TableCell align="center">
                    <Link
                      to={`/liga/tournament/${r.torneioId}/inscricao/${r.inscricaoId}/editar?tab=0`}
                      style={linkStyle}
                      title="Editar inscrição"
                    >
                      <EditIcon fontSize="small" />
                    </Link>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredRows.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Itens por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
          }
        />
      </TableContainer>
    </>
  );
};

export default LigaInscricoesTab;
